import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Star, Check, X, Trash2, Loader2, RefreshCw, Inbox } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StarsDisplay({ rating, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? "fill-yellow-400 text-yellow-400" : "text-neutral-700"}
        />
      ))}
    </span>
  );
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function ReviewsAdminView() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pendentes"); // pendentes | aprovadas | todas

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reviews?limit=500`);
      setReviews(res.data || []);
    } catch (e) {
      console.error("admin reviews error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (id, aprovado) => {
    try {
      await axios.patch(`${API}/reviews/${id}`, { aprovado });
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, aprovado } : r)));
    } catch (e) {
      console.error("approve error", e);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar esta avaliação? Não pode ser revertido.")) return;
    try {
      await axios.delete(`${API}/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error("delete error", e);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "pendentes") return reviews.filter((r) => !r.aprovado);
    if (filter === "aprovadas") return reviews.filter((r) => r.aprovado);
    return reviews;
  }, [reviews, filter]);

  const counts = useMemo(
    () => ({
      pendentes: reviews.filter((r) => !r.aprovado).length,
      aprovadas: reviews.filter((r) => r.aprovado).length,
      todas: reviews.length,
    }),
    [reviews]
  );

  return (
    <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="reviews-admin-view">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-10 bg-yellow-400" />
            <span className="overline">Moderação · Site público</span>
          </div>
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-tight">
            <Inbox className="inline mr-3 text-yellow-400" size={32} />
            Avaliações
          </h2>
          <p className="text-sm text-neutral-500 mt-2">
            Aprova ou rejeita as avaliações antes de aparecerem na página pública.
          </p>
        </div>
        <button
          onClick={load}
          className="btn-ghost text-xs inline-flex items-center gap-2"
          data-testid="reviews-admin-refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "pendentes", label: `Pendentes · ${counts.pendentes}` },
          { id: "aprovadas", label: `Aprovadas · ${counts.aprovadas}` },
          { id: "todas", label: `Todas · ${counts.todas}` },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className="chip"
            data-active={filter === f.id}
            data-testid={`reviews-admin-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card p-10 text-center text-neutral-400">
          <Loader2 className="animate-spin mx-auto mb-2" /> A carregar...
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-10 text-center text-neutral-500" data-testid="reviews-admin-empty">
          {filter === "pendentes"
            ? "Nenhuma avaliação pendente. Tudo em dia 👌"
            : filter === "aprovadas"
              ? "Ainda não tens avaliações aprovadas."
              : "Sem avaliações."}
        </div>
      ) : (
        <div className="grid gap-4" data-testid="reviews-admin-list">
          {filtered.map((r) => (
            <div key={r.id} className="card p-5" data-testid={`review-admin-card-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h3 className="font-display text-lg uppercase tracking-tight">{r.nome}</h3>
                    <StarsDisplay rating={r.rating} />
                    {r.aprovado ? (
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 border rounded bg-green-400/15 text-green-300 border-green-400/40">
                        Aprovada
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 border rounded bg-yellow-400/15 text-yellow-300 border-yellow-400/40">
                        Pendente
                      </span>
                    )}
                    <span className="text-[11px] text-neutral-500">{fmtDate(r.created_at)}</span>
                  </div>
                  {r.comentario ? (
                    <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap">
                      “{r.comentario}”
                    </p>
                  ) : (
                    <p className="text-sm text-neutral-500 italic">(sem comentário escrito)</p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {r.aprovado ? (
                    <button
                      onClick={() => approve(r.id, false)}
                      className="btn-ghost text-xs inline-flex items-center gap-1.5"
                      data-testid={`review-unapprove-${r.id}`}
                      title="Reverter aprovação"
                    >
                      <X size={14} /> Reverter
                    </button>
                  ) : (
                    <button
                      onClick={() => approve(r.id, true)}
                      className="btn-primary text-xs inline-flex items-center gap-1.5"
                      data-testid={`review-approve-${r.id}`}
                    >
                      <Check size={14} /> Aprovar
                    </button>
                  )}
                  <button
                    onClick={() => remove(r.id)}
                    className="text-neutral-500 hover:text-red-400 p-2"
                    data-testid={`review-delete-${r.id}`}
                    aria-label="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
